import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, Search, LayoutDashboard, Map, CreditCard, LogOut, User, ChevronDown, ShieldCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/search', label: 'Search' },
    { path: '/map', label: 'Map' },
    { path: '/letters', label: 'Letters' },
    { path: '/planning-applications', label: 'Councils' },
    { path: '/planning-permission', label: 'Guides' },
    { path: '/pricing', label: 'Pricing' },
  ];

  return (
    <nav className="bg-white/80 backdrop-blur-xl border-b border-zinc-200/60 sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <div className="w-7 h-7 bg-emerald-700 rounded-md flex items-center justify-center">
              <Search className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-['Manrope'] font-extrabold text-zinc-900 text-[15px] tracking-tight">PlanningSearch</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                className={`px-3.5 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
                  isActive(path) 
                    ? 'bg-emerald-50 text-emerald-700' 
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50'
                }`}
              >
                {label}
              </Link>
            ))}

            <div className="w-px h-5 bg-zinc-200 mx-2" />

            {user && user !== false ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-zinc-50 transition-colors text-[13px] font-medium text-zinc-700" data-testid="user-menu-trigger">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-emerald-700" />
                    </div>
                    <span className="max-w-[100px] truncate">{user.name || user.email}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border border-zinc-200 rounded-md shadow-lg">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer text-[13px]" data-testid="nav-dashboard-link">
                    <LayoutDashboard className="w-4 h-4 mr-2 text-zinc-400" /> Dashboard
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/p-admin')} className="cursor-pointer text-[13px]" data-testid="nav-admin-link">
                      <ShieldCheck className="w-4 h-4 mr-2 text-zinc-400" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-[13px] text-red-600 focus:text-red-600" data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="nav-login" className="px-3.5 py-1.5 text-[13px] font-medium text-zinc-500 hover:text-zinc-900 rounded-md hover:bg-zinc-50 transition-colors">
                  Sign in
                </Link>
                <Link to="/signup" data-testid="nav-signup" className="px-4 py-1.5 bg-emerald-700 text-white text-[13px] font-semibold rounded-md hover:bg-emerald-800 transition-colors">
                  Get started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden text-zinc-600" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-zinc-100 mt-1" data-testid="mobile-menu">
            {navLinks.map(({ path, label }) => (
              <Link key={path} to={path} onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-2.5 text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 rounded-md mt-1">
                {label}
              </Link>
            ))}
            {user && user !== false ? (
              <>
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-zinc-600 hover:bg-zinc-50 rounded-md">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                {user.role === 'admin' && (
                  <Link to="/p-admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-zinc-600 hover:bg-zinc-50 rounded-md" data-testid="mobile-admin-link">
                    <ShieldCheck className="w-4 h-4" /> Admin
                  </Link>
                )}
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-md w-full text-left">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </>
            ) : (
              <div className="flex gap-2 px-3 pt-2">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="px-4 py-2 text-sm text-zinc-600 border border-zinc-200 rounded-md">Sign in</Link>
                <Link to="/signup" onClick={() => setMobileOpen(false)} className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-md">Get started</Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
